import { useEffect, useState } from 'react';
import { Users, FileText, Building2, ChevronLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { authFetch } from '../services/api';
import { formatCurrency } from '../utils/formatters';

function InvestorList({ investors, onPick }) {
  if (investors.length === 0) {
    return (
      <div className="text-center py-20">
        <Users size={48} className="text-slate-700 mx-auto mb-3" />
        <p className="text-slate-500">No investors are sharing with you yet.</p>
      </div>
    );
  }
  return (
    <ul className="space-y-3">
      {investors.map((inv) => (
        <li
          key={inv.id}
          onClick={() => onPick(inv)}
          className="bg-slate-900 border border-slate-800 rounded-xl p-4 cursor-pointer hover:border-emerald-700"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white font-medium">{inv.full_name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{inv.email}</div>
            </div>
            <div className="text-right text-sm text-slate-400">
              {inv.property_count} {inv.property_count === 1 ? 'property' : 'properties'}
              {inv.has_personal_docs && (
                <div className="text-xs text-emerald-400 mt-0.5">+ identity vault</div>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function InvestorDetail({ investor, onBack }) {
  const [activeTab, setActiveTab] = useState('properties');
  const [properties, setProperties] = useState(null);
  const [vault, setVault] = useState(null);

  useEffect(() => {
    authFetch(`/api/sharing/broker/investors/${investor.id}/properties`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setProperties);
    if (investor.has_personal_docs) {
      authFetch(`/api/sharing/broker/investors/${investor.id}/personal-docs`)
        .then((r) => (r.ok ? r.json() : []))
        .then(setVault);
    }
  }, [investor.id, investor.has_personal_docs]);

  const viewDoc = async (docId) => {
    const res = await authFetch(
      `/api/sharing/broker/investors/${investor.id}/personal-docs/${docId}/url`
    );
    if (!res.ok) {
      toast.error('Could not open document');
      return;
    }
    const body = await res.json();
    window.open(body.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-4"
      >
        <ChevronLeft size={16} /> Back to investors
      </button>

      <div className="mb-4">
        <h2 className="text-xl font-semibold text-white">{investor.full_name}</h2>
        <div className="text-sm text-slate-500">{investor.email}</div>
      </div>

      <div className="flex border-b border-slate-800 mb-4">
        <button
          onClick={() => setActiveTab('properties')}
          className={`px-4 py-2 text-sm border-b-2 ${
            activeTab === 'properties'
              ? 'border-emerald-500 text-emerald-300'
              : 'border-transparent text-slate-400 hover:text-slate-200'
          }`}
        >
          <Building2 size={14} className="inline mr-1" /> Properties
        </button>
        {investor.has_personal_docs && (
          <button
            onClick={() => setActiveTab('vault')}
            className={`px-4 py-2 text-sm border-b-2 ${
              activeTab === 'vault'
                ? 'border-emerald-500 text-emerald-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <FileText size={14} className="inline mr-1" /> Verification documents
          </button>
        )}
      </div>

      {activeTab === 'properties' && (
        properties === null ? <p className="text-slate-500">Loading…</p> :
        properties.length === 0 ? <p className="text-slate-500">No properties.</p> :
        <ul className="space-y-3">
          {properties.map((p) => (
            <li key={p.id} className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <div className="text-white font-medium">{p.address_line1}</div>
              <div className="text-xs text-slate-500">{p.town}, {p.postcode} · {p.property_type} · {p.bedrooms} bed</div>
              {p.latest_valuation && (
                <div className="mt-2 text-sm text-emerald-300">
                  {formatCurrency(p.latest_valuation.range_low)} — {formatCurrency(p.latest_valuation.range_high)}
                  <span className="text-xs text-slate-500 ml-2">midpoint {formatCurrency(p.latest_valuation.range_mid)}</span>
                </div>
              )}
              {p.outstanding_debt > 0 && (
                <div className="mt-1 text-xs text-amber-300">
                  Debt {formatCurrency(p.outstanding_debt)}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {activeTab === 'vault' && investor.has_personal_docs && (
        vault === null ? <p className="text-slate-500">Loading…</p> :
        <div className="space-y-3">
          {vault.map((g) => (
            <div key={g.key} className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <h3 className="text-white font-medium mb-2">{g.label}</h3>
              {g.documents.length === 0 ? (
                <p className="text-sm text-slate-500">Not provided.</p>
              ) : (
                <ul className="space-y-1.5">
                  {g.documents.map((d) => (
                    <li key={d.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-300 truncate">{d.filename}</span>
                      <button
                        onClick={() => viewDoc(d.id)}
                        className="text-emerald-400 hover:text-emerald-300 text-xs px-2 py-1"
                      >
                        View
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BrokerShared() {
  const [investors, setInvestors] = useState(null);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    authFetch('/api/sharing/broker/investors')
      .then((r) => (r.ok ? r.json() : []))
      .then(setInvestors);
  }, []);

  if (investors === null) {
    return <div className="p-6 text-slate-500">Loading…</div>;
  }

  return (
    <div className="p-6 max-w-5xl">
      {picked ? (
        <InvestorDetail investor={picked} onBack={() => setPicked(null)} />
      ) : (
        <>
          <h1 className="text-2xl font-bold text-white mb-1">Investors sharing with you</h1>
          <p className="text-slate-400 text-sm mb-5">
            Read-only portfolios shared by investors on PropScope. Click any investor
            to view their properties and (if shared) verification documents.
          </p>
          <InvestorList investors={investors} onPick={setPicked} />
        </>
      )}
    </div>
  );
}
